/**
 * Fetch a Shopify section URL and return its inner HTML.
 * Optionally strips a lazy-load split marker (e.g. T4S `[t4splitlz]`).
 * @param {string} url
 * @param {object} [opts]
 * @param {string} [opts.splitMarker='[t4splitlz]'] - Marker to split on; set to `null` to skip.
 * @returns {Promise<string>}
 */
async function pdpFetchSection(url, { splitMarker = '[t4splitlz]' } = {}) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(res.status);
  let html = await res.text();
  if (splitMarker && html.indexOf(splitMarker) > -1) html = html.split(splitMarker)[1] || html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.querySelector('.shopify-section') || doc.body).innerHTML;
}


const SELECTOR_ITEM = '.anchor-nav__item';
const SELECTOR_ITEMS = '.anchor-nav__items';
const SELECTOR_SECTION = '.anchor-nav';

class AnchorNav extends HTMLElement {
  constructor() {
    super();
    this.navItems = [];
    this.activeIndex = 0;
    this.isScrolling = false;
    this.observer = null;
    this._stickyObserver = null;
    this._scrollEndTimer = null;
  }

  connectedCallback() {
    this.navItems = [...this.querySelectorAll(SELECTOR_ITEM)];
    this.navWrapper = this.querySelector(SELECTOR_ITEMS);
    if (!this.navItems.length || !this.navWrapper) return;

    this.sectionEl = this.closest(SELECTOR_SECTION);
    this.headerSelector = this.dataset.headerSelector || '.t4s-section-header';
    this.mainSelector = this.dataset.mainSelector || '#MainContent';
    this.rootMarginBottom = this.dataset.rootMarginBottom || '-60%';
    this.scrollBehavior = this.dataset.scrollBehavior === 'auto' ? 'auto' : 'smooth';

    this._ensureSlider();
    this.updateSlider(this.navItems[this.activeIndex]);

    this._bindClick();
    this._bindGlobalScroll();
    this._bindResize();
    this._bindHeaderEvents();
    this._initStickyDetect();
    this._initBottomMode();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._initObserver(), { once: true });
    } else {
      this._initObserver();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this._stickyObserver?.disconnect();
    if (this._handleBottomMode) document.removeEventListener('scroll', this._handleBottomMode);
    if (this._handleGlobalScroll) document.removeEventListener('scroll', this._handleGlobalScroll);
    if (this._handleResize) window.removeEventListener('resize', this._handleResize);
    if (this._handleHeaderResize) window.removeEventListener('resize', this._handleHeaderResize);
    if (this._sentinel) this._sentinel.remove();
  }

  /* ---------- public ---------- */

  setActive(index) {
    if (index < 0 || index >= this.navItems.length) return;
    const next = this.navItems[index];
    if (index === this.activeIndex && next?.classList.contains('active')) return;
    this.navItems.forEach((item) => {
      item.classList.remove('active');
      item.removeAttribute('aria-current');
    });
    this.activeIndex = index;
    next?.classList.add('active');
    next?.setAttribute('aria-current', 'true');
    this.updateSlider(next);
    this.dispatchEvent(
      new CustomEvent('anchor-nav:change', {
        detail: { index, item: next, id: next?.getAttribute('href')?.substring(1) },
        bubbles: true,
      })
    );
  }

  scrollToIndex(index) {
    const item = this.navItems[index];
    const id = item?.getAttribute('href')?.substring(1);
    const el = id ? document.getElementById(id) : null;
    if (!el) return;
    this.isScrolling = true;
    this.setActive(index);
    this._centerActiveItem();
    this._headerEl?.dispatchEvent(new Event('preventHeaderReveal'));
    const offset = item.hasAttribute('data-no-offset') ? 0 : this._getOffset();
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - offset,
      behavior: this.scrollBehavior,
    });
  }

  /* ---------- internals ---------- */

  _ensureSlider() {
    let slider = this.navWrapper.querySelector('.anchor-nav__slider');
    if (!slider) {
      slider = document.createElement('div');
      slider.classList.add('anchor-nav__slider');
      this.navWrapper.appendChild(slider);
    }
    this.slider = slider;
  }

  _getOffset() {
    if (!this.sectionEl) return 0;
    return this.sectionEl.offsetHeight + (parseInt(getComputedStyle(this.sectionEl).top, 10) || 0);
  }

  updateSlider(button) {
    if (!button || !this.slider || !this.navWrapper) return;
    const wrapperWidth = this.navWrapper.offsetWidth;
    if (!wrapperWidth) return;
    const left = ((button.offsetLeft / wrapperWidth) * 100).toFixed(2);
    const right = (100 - ((button.offsetLeft + button.offsetWidth) / wrapperWidth) * 100).toFixed(2);
    this.slider.style.clipPath = `inset(0 ${right}% 0 ${left}%)`;
  }

  _centerActiveItem() {
    const active = this.navItems[this.activeIndex];
    if (!active) return;
    const target = active.offsetLeft - (this.clientWidth - active.offsetWidth) / 2;
    this.scrollTo({ left: Math.max(0, target), behavior: this.scrollBehavior });
  }

  _initObserver() {
    this.observer?.disconnect();
    const offset = this._getOffset();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.isScrolling) return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = this.navItems.findIndex((i) => i.getAttribute('href') === `#${entry.target.id}`);
          if (index === -1) continue;
          this.setActive(index);
          this._centerActiveItem();
        }
      },
      { rootMargin: `-${offset}px 0px ${this.rootMarginBottom} 0px`, threshold: 0 }
    );
    this.navItems.forEach((item) => {
      const id = item.getAttribute('href')?.substring(1);
      const el = id ? document.getElementById(id) : null;
      if (el) this.observer.observe(el);
    });
  }

  _bindClick() {
    this.navWrapper.addEventListener('click', (e) => {
      const item = e.target.closest(SELECTOR_ITEM);
      if (!item) return;
      const index = this.navItems.indexOf(item);
      if (index === -1) return;
      e.preventDefault();
      this.scrollToIndex(index);
    });
  }

  _bindGlobalScroll() {
    this._handleGlobalScroll = () => {
      if (!this.isScrolling) return;
      clearTimeout(this._scrollEndTimer);
      this._scrollEndTimer = setTimeout(() => {
        this.isScrolling = false;
      }, 150);
    };
    document.addEventListener('scroll', this._handleGlobalScroll, { passive: true });
  }

  _bindResize() {
    this._handleResize = () => {
      this._initObserver();
      this.updateSlider(this.navItems[this.activeIndex]);
    };
    window.addEventListener('resize', this._handleResize, { passive: true });
  }

  _bindHeaderEvents() {
    this._headerEl = document.querySelector(this.headerSelector);
    const readHeaderHeight = () => {
      this.headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0px';
    };
    if (window.luteUtil?.lazyLoad) {
      window.luteUtil.lazyLoad(readHeaderHeight);
    } else {
      readHeaderHeight();
    }

    if (!this._headerEl || !this.sectionEl) return;
    this._headerEl.addEventListener('T4sHeaderHide', () => {
      this.sectionEl.style.setProperty('--sticky-top', '0px');
      requestAnimationFrame(() => {
        this._initObserver();
        this._refreshBottomModeCache?.();
      });
    });
    this._headerEl.addEventListener('T4sHeaderReveal', () => {
      this.sectionEl.style.setProperty('--sticky-top', this.headerHeight);
      requestAnimationFrame(() => {
        this._initObserver();
        this._refreshBottomModeCache?.();
      });
    });
    this._handleHeaderResize = readHeaderHeight;
    window.addEventListener('resize', this._handleHeaderResize, { passive: true });
  }

  _initStickyDetect() {
    if (!this.sectionEl) return;
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'height:0;width:0;pointer-events:none;visibility:hidden';
    this.sectionEl.parentNode.insertBefore(sentinel, this.sectionEl);
    this._sentinel = sentinel;
    this._stickyObserver = new IntersectionObserver(
      ([entry]) => {
        this.sectionEl.classList.toggle('is-stuck', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    this._stickyObserver.observe(sentinel);
  }

  _initBottomMode() {
    this.isFixedTop = false;
    const mainContent = document.querySelector(this.mainSelector);
    if (!mainContent || !this.sectionEl) return;

    // Cache layout-dependent values; refresh only on resize / header events.
    let navHeight = this.sectionEl.offsetHeight;
    let stickyTop = parseInt(getComputedStyle(this.sectionEl).getPropertyValue('--sticky-top'), 10) || 0;
    const refreshCache = () => {
      navHeight = this.sectionEl.offsetHeight;
      stickyTop = parseInt(getComputedStyle(this.sectionEl).getPropertyValue('--sticky-top'), 10) || 0;
    };
    this._refreshBottomModeCache = refreshCache;

    let rafId = 0;
    this._handleBottomMode = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const rect = mainContent.getBoundingClientRect();
        const shouldPin = rect.bottom < stickyTop + navHeight;
        if (shouldPin !== this.isFixedTop) {
          this.isFixedTop = shouldPin;
          this.sectionEl.classList.toggle('is-fixed-top', shouldPin);
        }
      });
    };
    document.addEventListener('scroll', this._handleBottomMode, { passive: true });
    window.addEventListener('resize', refreshCache, { passive: true });
    this._handleBottomMode();
  }
}
if (!customElements.get('anchor-nav')) customElements.define('anchor-nav', AnchorNav);

const BASE_URL = window.luteUtil?.getApiUrl()
const DEFAULT_ENDPOINT = `${BASE_URL}/api/station/activity/task/shopify/taskEventReport`;

class TaskEventTracker extends HTMLElement {
  #atcHandler = null;
  #cartSuccessHandler = null;
  #loginHandler = null;
  #pendingCart = false;
  #pendingReports = [];

  connectedCallback() {
    if (!this.dataset.customer) return;
    if (!window.__taskEventTrackerListenerAttached) {
      this.#initAtcTracking();
      window.__taskEventTrackerListenerAttached = true;
    }
    if (!window.taskEventTracker) {
      window.taskEventTracker = {
        report: (eventType, extra) => this.report(eventType, extra),
        instance: this,
      };
    }
    this.#loginHandler = () => this.#flushPending();
    document.addEventListener('LUTE:AUTH:LOGIN', this.#loginHandler);
    this.#reportPageView();
  }

  disconnectedCallback() {
    if (this.#atcHandler) {
      document.removeEventListener('click', this.#atcHandler, true);
      this.#atcHandler = null;
    }
    if (this.#cartSuccessHandler) {
      const $ = window.jQuery_T4NT || window.jQuery;
      $?.(document).off('add:cart:success', this.#cartSuccessHandler);
      this.#cartSuccessHandler = null;
    }
    if (this.#loginHandler) {
      document.removeEventListener('LUTE:AUTH:LOGIN', this.#loginHandler);
      this.#loginHandler = null;
    }
    window.__taskEventTrackerListenerAttached = false;
    if (window.taskEventTracker?.instance === this) {
      delete window.taskEventTracker;
    }
  }

  get isLoggedIn() {
    return !!(window.__MOMCOZY_USER_INFO__ && window.__MOMCOZY_USER_INFO__.email);
  }

  #flushPending() {
    if (!this.isLoggedIn || !this.#pendingReports.length) return;
    const queue = this.#pendingReports.splice(0);
    queue.forEach(({ eventType, extra }) => this.report(eventType, extra));
  }

  get endpoint() {
    return this.dataset.endpoint || DEFAULT_ENDPOINT;
  }

  get email() {
    return this.dataset.email || window?.__MOMCOZY_USER_INFO__?.email;
  }

  report(eventType, extra = {}) {
    if (!eventType) return;
    if (!this.isLoggedIn) {
      this.#pendingReports.push({ eventType, extra });
      return;
    }
    const body = {
      eventType,
      url: extra.url ?? window.location.href,
      email: extra.email ?? this.email ?? undefined,
      orderId: extra.orderId ?? undefined,
    };
    return this.#send(body);
  }

  #send(body) {
    return fetch(this.endpoint, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${window.__MOMCOZY_USER_INFO__?.token || ''}`,
      },
      body: JSON.stringify(body),
    }).catch((err) => {
      console.warn('[task-event-tracker] report failed:', err);
    });
  }

  #reportPageView() {
    const eventType = this.dataset.pageView;
    if (!eventType) return;
    this.report(eventType);
  }

  /**
   * 监听 ATC 点击 → 暂存待上报标记；
   * 主题在加购请求成功后会触发 jQuery `add:cart:success`，到时才真正上报。
   */
  #initAtcTracking() {
    this.#atcHandler = (e) => {
      const target = e.target;
      if (target.closest('[data-action-atc], [data-atc-form]')) {
        this.#pendingCart = true;
      }
    };
    document.addEventListener('click', this.#atcHandler, true);

    const $ = window.jQuery_T4NT || window.jQuery;
    if (!$) return;
    this.#cartSuccessHandler = () => {
      if (!this.#pendingCart) return;
      this.#pendingCart = false;
      const cartEventType = 2
      this.report(cartEventType);
    };
    $(document).on('add:cart:success', this.#cartSuccessHandler);
  }
}
if (!customElements.get('task-event-tracker')) customElements.define('task-event-tracker', TaskEventTracker);

// ─── ExternalVideo custom element ─────────────────────────────────────────────

class ExternalVideo extends HTMLElement {
  #hasLoaded = false;
  #isVisible = false;
  #isPlaying = false;
  #shouldPlay = false;
  #setupPromise = undefined;
  #removeClickListener = undefined;
  #visibilityObserver = undefined;

  connectedCallback() {
    this.#observePlaybackVisibility();
    if (this.autoPlay) return;
    const playOnClick = () => void this.play();
    this.addEventListener("click", playOnClick, { once: true });
    this.#removeClickListener = () => this.removeEventListener("click", playOnClick);
  }

  disconnectedCallback() {
    this.#removeClickListener?.();
    this.#visibilityObserver?.disconnect();
    if (this.#messageListener) window.removeEventListener("message", this.#messageListener);
  }

  get autoPlay() { return this.hasAttribute("autoplay"); }
  get playing() { return this.#isPlaying; }
  get wantsPlayback() { return this.#shouldPlay; }
  get visible() { return this.#isVisible; }
  get provider() { return this.getAttribute("provider") ?? "video"; }

  preload() {
    if (!this.#hasLoaded) void this.#setupPlayer();
  }

  static #YOUTUBE_ORIGIN = "https://www.youtube.com";
  #youtubeReady = false;
  #hasInitializedYouTubeListener = false;
  #pendingAction = null;
  #messageListener = undefined;

  async play() {
    this.#shouldPlay = true;
    if (!this.#hasLoaded) await this.#setupPlayer();
    if (!this.#shouldPlay) return;
    if (!this.#isVisible) return;

    if (this.provider === "youtube") {
      if (!this.#youtubeReady) { this.#pendingAction = "play"; return; }
      this.#isPlaying = true;
      this.querySelector("iframe")?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo", args: "" }),
        ExternalVideo.#YOUTUBE_ORIGIN,
      );
      return;
    }

    if (!this.#isVisible) return;
    this.#isPlaying = true;

    if (this.provider === "vimeo") {
      this.querySelector("iframe")?.contentWindow?.postMessage(JSON.stringify({ method: "play" }), "*");
      return;
    }

    await this.querySelector("video")?.play().catch(() => undefined);
  }

  pause() {
    this.#shouldPlay = false;
    this.#isPlaying = false;
    if (!this.#hasLoaded) return;

    if (this.provider === "youtube") {
      if (!this.#youtubeReady) { this.#pendingAction = "pause"; return; }
      this.querySelector("iframe")?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "pauseVideo", args: "" }),
        ExternalVideo.#YOUTUBE_ORIGIN,
      );
      return;
    }

    if (this.provider === "vimeo") {
      this.querySelector("iframe")?.contentWindow?.postMessage(JSON.stringify({ method: "pause" }), "*");
      return;
    }

    this.querySelector("video")?.pause();
  }

  #flushPendingAction() {
    if (!this.#youtubeReady || !this.#pendingAction) return;
    const action = this.#pendingAction;
    this.#pendingAction = null;
    if (action === "play") void this.play();
    else this.pause();
  }

  #observePlaybackVisibility() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#isVisible = entry.isIntersecting;
      if (!entry.isIntersecting) { this.pause(); return; }
      if (!this.#hasLoaded) void this.#setupPlayer();
      if (this.autoPlay) void this.play();
    }, { threshold: 0 });
    this.#visibilityObserver.observe(this);
  }

  #setupPlayer() {
    if (this.#setupPromise) return this.#setupPromise;
    this.#setupPromise = new Promise((resolve) => {
      const template = this.querySelector("template");
      const node = template?.content.firstElementChild?.cloneNode(true);
      if (!template || !node) { resolve(); return; }

      const player = node;

      if (player instanceof HTMLIFrameElement && this.provider === "youtube") {
        const playerUrl = new URL(player.src);
        playerUrl.searchParams.set("origin", location.origin);
        player.src = playerUrl.toString();
      }

      const markLoaded = () => { this.#hasLoaded = true; resolve(); };

      const initYouTubeApi = () => {
        if (!(player instanceof HTMLIFrameElement) || this.provider !== "youtube") return;
        if (!this.#hasInitializedYouTubeListener) {
          this.#hasInitializedYouTubeListener = true;
          this.#messageListener = (event) => {
            if (event.source !== player.contentWindow) return;
            let data;
            try { data = JSON.parse(event.data); } catch { return; }
            if (data.event === "onReady") { this.#youtubeReady = true; this.#flushPendingAction(); }
          };
          window.addEventListener("message", this.#messageListener);
        }
        player.contentWindow?.postMessage(
          JSON.stringify({ event: "listening" }),
          ExternalVideo.#YOUTUBE_ORIGIN,
        );
      };

      if (player instanceof HTMLIFrameElement || player instanceof HTMLVideoElement) {
        player.addEventListener(player instanceof HTMLVideoElement ? "loadedmetadata" : "load", () => {
          initYouTubeApi(); markLoaded();
        }, { once: true });
        player.addEventListener("error", markLoaded, { once: true });
      } else {
        queueMicrotask(markLoaded);
      }

      if (this.autoPlay) { template.replaceWith(player); }
      else { this.replaceChildren(player); }

      initYouTubeApi();
    });
    return this.#setupPromise;
  }
}

if (!customElements.get("external-video")) customElements.define("external-video", ExternalVideo);

// ── helpers ──────────────────────────────────────────────────────────────

const PREFIX = 'shopify-section-';

/**
 * Strip the "shopify-section-" prefix if present.
 * @param {string} id
 * @returns {string}
 */
function normalizeSectionId(id) {
  return id.startsWith(PREFIX) ? id.slice(PREFIX.length) : id;
}

/**
 * Build the Section Rendering API URL.
 * Appends `?section_id=<id>` (sorted to improve cache key stability).
 * @param {string} sectionId
 * @param {URL} [baseUrl]
 * @returns {string}
 */
function buildSectionURL(sectionId, baseUrl) {
  const url = new URL(baseUrl || window.location.href);
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort(); // keeps identical requests as one cache key
  return url.toString();
}

// ── SectionRenderer class ─────────────────────────────────────────────────

class SectionRenderer {
  constructor() {
    /** @type {Map<string, string>} url → cached html */
    this._cache = new Map();

    /** @type {Map<string, Promise<string>>} url → in-flight fetch promise */
    this._pending = new Map();

    /** @type {Map<string, AbortController>} sectionId → abort controller */
    this._abortControllers = new Map();

    // Seed cache from already-rendered page sections on load
    window.addEventListener('load', this._cachePageSections.bind(this), { once: true });
  }

  // ── public API ──────────────────────────────────────────────────────────

  /**
   * Fetch the rendered HTML for a section and replace the matching
   * `#shopify-section-<id>` element in the DOM via innerHTML swap.
   *
   * Cancels any in-progress render for the same section before starting.
   *
   * @param {string} sectionId
   * @param {object}  [options]
   * @param {URL}     [options.url]   - page URL to render for (default: current page)
   * @param {boolean} [options.cache] - use in-memory response cache
   * @returns {Promise<string>} the raw section HTML
   */
  async renderSection(sectionId, options) {
    this._abortPending(sectionId);

    const ac = new AbortController();
    this._abortControllers.set(sectionId, ac);

    let html = '';
    try {
      html = await this.getSectionHTML(sectionId, options, ac.signal);
    } catch (err) {
      if (ac.signal.aborted) return html;
      throw err;
    } finally {
      if (this._abortControllers.get(sectionId) === ac) {
        this._abortControllers.delete(sectionId);
      }
    }

    if (!ac.signal.aborted) {
      this._morphSection(sectionId, html);
    }

    return html;
  }

  /**
   * Fetch raw HTML for a section without touching the DOM.
   *
   * @param {string}  sectionId
   * @param {object}  [options]
   * @param {URL}     [options.url]   - page URL to render for (default: current page)
   * @param {boolean} [options.cache] - use in-memory response cache (default: !Shopify.designMode)
   * @param {AbortSignal} [signal]    - optional abort signal
   * @returns {Promise<string>}
   */
  async getSectionHTML(sectionId, options, signal) {
    const useCache = options?.cache ?? (typeof Shopify !== 'undefined' ? !Shopify.designMode : true);
    const sectionURL = buildSectionURL(sectionId, options?.url);

    if (useCache) {
      const cached = this._cache.get(sectionURL);
      if (cached != null) return cached;
    }

    // Deduplicate simultaneous identical fetches
    let pending = this._pending.get(sectionURL);
    if (pending) return pending;

    const fetchPromise = fetch(sectionURL, {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`SectionRenderer: ${r.status} for ${sectionURL}`);
        return r.text();
      })
      .then((html) => {
        this._cache.set(sectionURL, html);
        return html;
      })
      .finally(() => {
        this._pending.delete(sectionURL);
      });

    this._pending.set(sectionURL, fetchPromise);
    return fetchPromise;
  }

  /**
   * Invalidate the cached HTML for a section (all URL variants).
   * @param {string} sectionId
   */
  invalidate(sectionId) {
    const normalized = normalizeSectionId(sectionId);
    for (const key of this._cache.keys()) {
      // URL contains "section_id=<id>"
      if (key.includes('section_id=' + normalized)) {
        this._cache.delete(key);
      }
    }
  }

  // ── private ─────────────────────────────────────────────────────────────

  /**
   * Replace the inner content of the matching shopify-section wrapper.
   * Falls back to outerHTML swap when the wrapper is absent.
   * @param {string} sectionId
   * @param {string} html
   */
  _morphSection(sectionId, html) {
    const wrapperId = PREFIX + normalizeSectionId(sectionId);
    const existing = document.getElementById(wrapperId);

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const incoming = fragment.getElementById(wrapperId);

    if (existing && incoming) {
      existing.innerHTML = incoming.innerHTML;
      return;
    }

    // Section not yet in DOM (e.g. injected modal) — caller handles placement
  }

  /** Abort any in-progress render for `sectionId`. */
  _abortPending(sectionId) {
    const ac = this._abortControllers.get(sectionId);
    if (ac) {
      ac.abort();
      this._abortControllers.delete(sectionId);
    }
  }

  /**
   * On page load, seed the cache with the HTML of every section already
   * present in the DOM so the first re-render is instant.
   */
  _cachePageSections() {
    document.querySelectorAll('.shopify-section').forEach((el) => {
      const url = buildSectionURL(el.id);
      if (!this._cache.has(url)) {
        this._cache.set(url, el.outerHTML);
      }
    });
  }
}

// ── singleton export ──────────────────────────────────────────────────────

window.SectionRenderer = new SectionRenderer();

// ── SectionModalTrigger ───────────────────────────────────────────────────────
// Fetches a Shopify section via Section Rendering API and injects it into a DSM
// modal, then opens the modal. Migrated from b2g.js — usable by any feature.

function _smt_extractContent(html, sectionId, contentSel) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  if (contentSel) {
    const el = fragment.querySelector(contentSel);
    return el ? el.innerHTML : html;
  }
  const wrapper = fragment.getElementById('shopify-section-' + sectionId);
  return wrapper ? wrapper.innerHTML : html;
}

function _smt_getModalOverlay(modalId) {
  return (
    document.getElementById(modalId + '-overlay') ||
    document.querySelector(`[data-dsm-dialog="${CSS.escape(modalId)}"]`)
  );
}

function _smt_injectIntoModal(overlay, html, targetSel) {
  const target =
    (targetSel && overlay.querySelector(targetSel)) ||
    overlay.querySelector('.dsm-modal__content') ||
    overlay.querySelector('.dsm-modal__body') ||
    overlay;
  target.innerHTML = html;
}

/**
 * Extract <link rel="stylesheet"> tags from an HTML string, hoist them into
 * <head> (deduplicating by id then href), and return the cleaned HTML plus a
 * Promise that resolves once all newly-added sheets have loaded.
 * @param {string} html
 * @returns {{ html: string, promise: Promise<void> }}
 */
function _smt_hoistStyles(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));

  const pending = [];
  for (const link of links) {
    link.remove();

    const existing = link.id
      ? document.getElementById(link.id)
      : document.head.querySelector(`link[href="${link.href}"]`);

    if (existing) continue;

    const hoisted = link.cloneNode();
    pending.push(
      new Promise((resolve) => {
        hoisted.addEventListener('load', resolve, { once: true });
        hoisted.addEventListener('error', resolve, { once: true });
      })
    );
    document.head.appendChild(hoisted);
  }

  return { html: doc.body.innerHTML, promise: Promise.all(pending) };
}

class SectionModalTrigger extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    this.addEventListener('click', this._handleClick.bind(this), { signal: this._ac.signal });
  }

  disconnectedCallback() {
    this._ac?.abort();
  }

  get sectionId() { return this.getAttribute('section-id') || ''; }
  get modalId() { return this.getAttribute('modal-id') || ''; }
  get src() { return this.getAttribute('src') || window.location.href; }
  set src(v) { this.setAttribute('src', v); }
  get contentSel() { return this.getAttribute('content-sel') || null; }
  get useCache() { return this.getAttribute('cache') !== 'false'; }
  get loadingClass() { return this.getAttribute('loading-class') || 'is-loading'; }
  get targetSel() { return this.getAttribute('target-sel') || null; }
  get urlParameters() { return this.getAttribute('data-params') || ''; }

  async _handleClick(e) {
    if (e.target.closest('a[href], button[type="submit"], input, select, textarea')) return;
    e.preventDefault();

    const { sectionId, modalId } = this;
    if (!sectionId || !modalId) {
      console.warn('<section-modal-trigger>: section-id and modal-id are required');
      return;
    }

    const overlay = _smt_getModalOverlay(modalId);
    if (!overlay) {
      console.warn(`<section-modal-trigger>: modal "${modalId}" not found in DOM`);
      return;
    }

    this.dispatchEvent(new CustomEvent('section-modal:before-fetch', {
      bubbles: true,
      detail: { sectionId, modalId, url: this.src },
    }));

    overlay.classList.add(this.loadingClass);
    this.classList.add('is-loading');

    let html;
    try {
      html = await this._fetchSection(sectionId);
    } catch (err) {
      overlay.classList.remove(this.loadingClass);
      this.classList.remove('is-loading');
      this.dispatchEvent(new CustomEvent('section-modal:error', {
        bubbles: true,
        detail: { sectionId, modalId, error: err },
      }));
      console.error('<section-modal-trigger> fetch error:', err);
      return;
    }

    const content = _smt_extractContent(html, sectionId, this.contentSel);
    const { html: cleanHtml, promise: stylesReady } = _smt_hoistStyles(content);
    await stylesReady;
    _smt_injectIntoModal(overlay, cleanHtml, this.targetSel);

    overlay.classList.remove(this.loadingClass);
    this.classList.remove('is-loading');

    this.dispatchEvent(new CustomEvent('section-modal:after-fetch', {
      bubbles: true,
      detail: { sectionId, modalId, html: content },
    }));

    this._openModal(modalId);
  }

  _fetchSection(sectionId) {
    const fetchUrl = this._buildFetchUrl();
    const renderer = window.SectionRenderer;
    if (renderer) {
      return renderer.getSectionHTML(sectionId, { url: fetchUrl, cache: this.useCache });
    }
    fetchUrl.searchParams.set('section_id', sectionId);
    fetchUrl.searchParams.sort();
    return fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => {
        if (!r.ok) throw new Error(`section-modal-trigger: HTTP ${r.status}`);
        return r.text();
      });
  }

  _buildFetchUrl() {
    const url = new URL(this.src, window.location.origin);
    const extra = this.urlParameters;
    if (extra) new URLSearchParams(extra).forEach((v, k) => url.searchParams.set(k, v));
    return url;
  }

  _openModal(modalId) {
    if (window.DsmDialog) { window.DsmDialog.open(modalId); return; }
    const overlay = _smt_getModalOverlay(modalId);
    if (overlay) overlay.removeAttribute('hidden');
  }

  async open(overrideUrl) {
    if (overrideUrl) this.src = overrideUrl;
    await this._handleClick(new MouseEvent('click'));
  }
}

if (!customElements.get('section-modal-trigger')) {
  customElements.define('section-modal-trigger', SectionModalTrigger);
}

// ── VariantSwitcher ───────────────────────────────────────────────────────────
// Self-contained variant selector; works inside modals injected via Section
// Rendering API. Migrated from b2g.js — usable by any feature.

class VariantSwitcher extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.variants = this._readVariants();
    if (!this.variants.length) return;

    this.currentVariant = this._resolveInitialVariant();
    this._bindSwatches();
    this._updateSwatchAvailability();

    if (this.querySelector('[data-collapsible="true"]')) this._initCollapsible();
  }

  _initCollapsible() { }

  _onCollapseHeaderClick(header) {
    this.classList.toggle('is-closed');
  }

  _updateToggleLabel(variant) {
    const wrap = this.closest('.bgg-cart-card__options-wrap');
    const label = wrap?.querySelector('[data-options-toggle-label]');
    if (!label || !variant) return;
    const firstOptEl = this.querySelector('[data-pdp-option]');
    const optName = firstOptEl?.dataset.pdpOption ?? '';
    const optVal = variant.option1 ?? '';
    label.textContent = optName ? `${optName} \u2014 ${optVal}` : optVal;
  }

  _readVariants() {
    const giftId = this.dataset.giftId;

    // B2G consolidated map (only present when b2g.js is loaded)
    if (giftId) {
      const map = customElements.get('bgg-cart-modal')?.variantMapCache;
      if (map) {
        const entry = map[giftId];
        if (entry && Array.isArray(entry.variants)) return entry.variants;
      }
    }

    // Generic: [data-cart-variants-json] (product-picker-modal)
    const cartEl = this.querySelector('[data-cart-variants-json]');
    if (cartEl) {
      try { return JSON.parse(cartEl.textContent); } catch (_) { return []; }
    }

    // Legacy B2G: [data-bgg-modal-variants]
    const bggEl = this.querySelector('[data-bgg-modal-variants]');
    if (bggEl) {
      try { return JSON.parse(bggEl.textContent); } catch (_) { return []; }
    }

    // GWP consolidated map on ancestor: data-variant-switcher-variants keyed by giftId
    if (giftId) {
      const mapEl = this.closest('[data-gwp-gift-modal]')?.querySelector('[data-variant-switcher-variants]');
      if (mapEl) {
        try {
          const map = JSON.parse(mapEl.textContent);
          const entry = map[giftId];
          if (entry && Array.isArray(entry.variants)) return entry.variants;
        } catch (_) { }
      }
    }

    return [];
  }

  _resolveInitialVariant() {
    const opts = this.getSelectedOptions();
    if (opts.length) {
      const exactMatch = this.variants.find(v =>
        opts.every(o => v['option' + (o.index + 1)] === o.value)
      );
      if (exactMatch) return exactMatch;
    }
    return this.variants.find(v => v.available) || this.variants[0] || null;
  }

  _bindSwatches() {
    this.addEventListener('click', (e) => {
      const header = e.target.closest('.pdp-pf__style-header:has([data-collapsible="true"])');
      if (header) {
        this._onCollapseHeaderClick(header);
        return;
      }

      const swatch = e.target.closest('[data-pdp-swatch]');
      if (!swatch) return;
      if (swatch.classList.contains('is-soldout') && !swatch.classList.contains('is-cross')) return;

      const optionEl = swatch.closest('[data-pdp-option]');
      if (!optionEl) return;

      optionEl.querySelectorAll('[data-pdp-swatch]').forEach(s => {
        s.classList.remove('is-active');
        if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('is-active');
      if (swatch.hasAttribute('aria-pressed')) swatch.setAttribute('aria-pressed', 'true');

      const currentValueEl = optionEl.querySelector('[data-pdp-current-value]');
      if (currentValueEl) currentValueEl.textContent = swatch.dataset.value;

      this._onOptionChange();
    });
  }

  _onOptionChange() {
    const opts = this.getSelectedOptions();
    if (!opts.length) return;

    let newVariant = this.variants.find(v =>
      opts.every(o => v['option' + (o.index + 1)] === o.value)
    );

    const changedOpt = this.currentVariant
      ? opts.find(o => this.currentVariant['option' + (o.index + 1)] !== o.value)
      : null;

    // 精确匹配不存在或缺货时，在保持所选维度的前提下找最近的有货变体
    if (!newVariant || !newVariant.available) {
      const fallback = this.variants.find(v => {
        if (!v.available) return false;
        if (changedOpt && v['option' + (changedOpt.index + 1)] !== changedOpt.value) return false;
        return true;
      });

      if (fallback) {
        newVariant = fallback;
        const optionEls = Array.from(this.querySelectorAll('[data-pdp-option]'));
        optionEls.forEach(optionEl => {
          const idx = parseInt(optionEl.dataset.optionIndex, 10);
          const targetVal = fallback['option' + (idx + 1)];
          optionEl.querySelectorAll('[data-pdp-swatch]').forEach(s => {
            const active = s.dataset.value === targetVal;
            s.classList.toggle('is-active', active);
            if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', String(active));
          });
          const currentValueEl = optionEl.querySelector('[data-pdp-current-value]');
          if (currentValueEl) currentValueEl.textContent = targetVal;
        });
      } else if (!newVariant) {
        return;
      }
    }

    const oldVariant = this.currentVariant;
    this.currentVariant = newVariant;
    this._updateSwatchAvailability();
    this._updateToggleLabel(newVariant);

    this.dispatchEvent(new CustomEvent('variant-switcher:change', {
      bubbles: true,
      detail: { variant: newVariant, oldVariant },
    }));
  }

  _updateSwatchAvailability() {
    const optionEls = Array.from(this.querySelectorAll('[data-pdp-option]'));
    optionEls.forEach(optionEl => {
      const optionIndex = parseInt(optionEl.dataset.optionIndex, 10);
      optionEl.querySelectorAll('[data-pdp-swatch]').forEach(swatch => {
        const testValue = swatch.dataset.value;

        const strictlyAvailable = this.variants.some(v => {
          if (v['option' + (optionIndex + 1)] !== testValue) return false;
          return optionEls.every(otherEl => {
            const otherIndex = parseInt(otherEl.dataset.optionIndex, 10);
            if (otherIndex === optionIndex) return true;
            const otherActive = otherEl.querySelector('[data-pdp-swatch].is-active');
            return !otherActive || v['option' + (otherIndex + 1)] === otherActive.dataset.value;
          }) && v.available;
        });

        if (strictlyAvailable) {
          swatch.classList.remove('is-soldout', 'is-cross');
          return;
        }

        const anyAvailable = this.variants.some(v =>
          v['option' + (optionIndex + 1)] === testValue && v.available
        );
        swatch.classList.toggle('is-soldout', !anyAvailable);
        swatch.classList.toggle('is-cross', anyAvailable);
      });
    });
  }

  getSelectedOptions() {
    const opts = [];
    this.querySelectorAll('[data-pdp-option]').forEach(optionEl => {
      const active = optionEl.querySelector('[data-pdp-swatch].is-active');
      if (active) {
        opts.push({
          index: parseInt(optionEl.dataset.optionIndex, 10),
          value: active.dataset.value,
        });
      }
    });
    return opts;
  }
}

if (!customElements.get('variant-switcher')) {
  customElements.define('variant-switcher', VariantSwitcher);
}

/* ===== BggVariantCard — syncs product image & price when variant-switcher changes ===== */
if (!customElements.get('bgg-variant-card')) {
  class BggVariantCard extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.addEventListener('variant-switcher:change', (e) => this._onVariantChange(e.detail.variant));
    }

    _onVariantChange(variant) {
      if (!variant) return;

      const imgEl = this.querySelector('[data-modal-img]');
      if (imgEl) {
        const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
        if (src) imgEl.src = src;
      }

      const priceEl = this.querySelector('[data-variant-price]');
      if (priceEl) {
        const fmt = (price) => {
          if (window.T4SThemeSP?.Currency?.formatMoney) {
            return window.T4SThemeSP.Currency.formatMoney(price, "${{amount}}");
          }
          return (price / 100).toFixed(2);
        };

        if (this.dataset.addon === 'true') {
          // 顺手买一件：显示商品自身现价 + compare_at_price 划线
          if (variant.price > 0) {
            priceEl.textContent = fmt(variant.price);
            priceEl.hidden = false;
          } else {
            priceEl.hidden = true;
          }
          const origEl = this.querySelector('[data-variant-original-price]');
          if (origEl) {
            if (variant.compare_at_price > variant.price) {
              origEl.textContent = fmt(variant.compare_at_price);
              origEl.hidden = false;
            } else {
              origEl.hidden = true;
            }
          }
        } else {
          // 买赠：只更新划线原价，FREE 文字保持不变
          if (variant.price > 0) {
            priceEl.textContent = fmt(variant.price);
            priceEl.hidden = false;
          } else {
            priceEl.hidden = true;
          }
        }
      }
    }
  }

  customElements.define('bgg-variant-card', BggVariantCard);
}

// ── CartVariantModal ──────────────────────────────────────────────────────────
// 弹窗内容容器；监听 DsmDialog confirm，将选中 variantId + qty + cartKey 打包为
// cart:variant-update 事件向上冒泡，解耦弹窗内部 DOM 与消费方（CartComponent）
class CartVariantModal extends HTMLElement {
  connectedCallback() {
    if (this._ac) return;
    this._ac = new AbortController();
    document.addEventListener('dsm:dialog:confirm', this, { signal: this._ac.signal });
    this.addEventListener('variant-switcher:change', this, { signal: this._ac.signal });
  }

  disconnectedCallback() {
    this._ac?.abort();
    this._ac = null;
  }

  /** 收集当前弹窗内的选择数据，无效时返回 null */
  collect() {
    const switcher = this.querySelector('[data-variant-switcher]');
    // 无 variant-switcher 时（单规格商品）回退到元素上的 data-variant-id
    const variantId = switcher?.currentVariant?.id ?? this.dataset.variantId;
    if (!variantId) return null;
    const cartKey = this.dataset.cartKey || null;
    const qty = parseInt(this.querySelector('[data-quantity-value]')?.value || '1', 10);
    return { cartKey, variantId, qty };
  }

  handleEvent(e) {
    if (e.type === 'variant-switcher:change') {
      this.#updateDisplay(e.detail.variant);
      return;
    }
    // 仅响应包含本元素的 overlay 上的 confirm
    if (!e.target.closest('[data-dsm-dialog]')?.contains(this)) return;

    const data = this.collect();
    if (!data) return;

    this.dispatchEvent(new CustomEvent('cart:variant-update', {
      bubbles: true,
      detail: data,
    }));
  }

  #updateDisplay(variant) {
    if (!variant) return;
    this.dataset.variantId = String(variant.id);

    // 图片
    const img = this.querySelector('[data-product-img]');
    if (img && variant.featured_image?.src) {
      img.src = variant.featured_image.src;
      if (variant.featured_image.alt) img.alt = variant.featured_image.alt;
      const placeholder = this.querySelector('[data-product-img-placeholder]');
      if (placeholder) placeholder.remove();
    }

    // 价格
    const fmt = window.T4SThemeSP?.Currency?.formatMoney;
    const integerEl = this.querySelector('[data-product-price]');
    const compareEl = this.querySelector('[data-product-compare-price]');
    if (integerEl && fmt) {
      integerEl.textContent = fmt(variant.price, "{{amount}}");
    }
    if (compareEl && variant.compare_at_price) {
      if (fmt) compareEl.textContent = fmt(variant.compare_at_price, "${{amount}}");
      compareEl.hidden = false;
    } else if (compareEl) {
      compareEl.hidden = true;
    }

    // 标题（有的 variant 带 title，如 "Size: M"）
    const titleEl = this.querySelector('[data-product-title]');
    if (titleEl && variant.title && variant.title !== 'Default Title') {
      titleEl.textContent = variant.title;
    }
  }

}

if (!customElements.get('product-picker-modal')) {
  customElements.define('product-picker-modal', CartVariantModal);
}


/* ===== PDP Info Modal Web Component ===== */
if (!customElements.get('pdp-info-modal')) {
  class PdpInfoModal extends HTMLElement {
    constructor() {
      super();
      this._overlay = null;
      this._titleEl = null;
      this._bodyEl = null;
      this._slots = {};
      this._customClass = []
    }

    static get observedAttributes() {
      return ['open'];
    }

    connectedCallback() {
      this._slots = {};
      for (const tpl of this.querySelectorAll('template')) {
        const name = tpl.getAttribute('slot') || 'body';
        this._slots[name] = tpl.innerHTML;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this);
      if (this._overlay) {
        this._overlay.removeEventListener('click', this);
        this._overlay.remove();
        this._overlay = null;
      }
    }

    /* — handleEvent: single event router — */
    handleEvent(e) {
      switch (e.type) {
        case 'click':
          if (e.target === this._overlay || e.target.closest('[data-close]')) {
            this.open = false;
          }
          break;
        case 'keydown':
          if (e.key === 'Escape') this.open = false;
          else if (e.key === 'Tab') this._trapFocus(e);
          break;
        case 'transitionend':
          if (e.target === this._overlay && !this.open) {
            this._afterClose();
          }
          break;
      }
    }

    /* — Attribute-driven state — */
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (name === 'open') {
        newValue !== null ? this._onShow() : this._onClose();
      }
    }

    get open() {
      return this.hasAttribute('open');
    }

    get contentElement() {
      return this._bodyEl;
    }

    get contentElement() {
      return this._bodyEl;
    }

    set open(val) {
      val ? this.setAttribute('open', '') : this.removeAttribute('open');
    }

    /* — Public API (matches <dialog> convention) — */
    show() {
      if (this.open) {
        this._onShow();
      } else {
        this.open = true;
      }
    }

    close() {
      this.open = false;
    }

    setTitle(text) {
      this.dataset.title = text;
      if (this._titleEl) this._titleEl.textContent = text;
    }

    setContent(html, className = '') {
      const isSameContent = this._slots.body === html;
      this._slots.body = html;
      if (this._bodyEl) {
        if (!isSameContent) this._bodyEl.innerHTML = html;
        this._bodyEl.className = `pdp-info-modal__body ${className}`;
      }
    }

    setClass(className) {
      this._customClass = className ? className.split(/\s+/).filter(Boolean) : [];
      if (this._overlay) {
        this._overlay.classList.add(...this._customClass);
      }
    }

    /* — Private helpers — */
    _onShow() {
      this._triggerEl = document.activeElement;
      this._ensureOverlay();
      requestAnimationFrame(() => {
        this._overlay.classList.add('is-open');
        document.documentElement.classList.add('pdp-scroll-lock');
        document.addEventListener('keydown', this);
        /* Focus the close button or first focusable element */
        const firstFocusable = this._overlay.querySelector('[data-close], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();
      });
      this.dispatchEvent(new CustomEvent('pdp-info-modal:toggle', { bubbles: true, detail: { open: true } }));
    }

    _onClose() {
      if (!this._overlay) return;
      this._overlay.classList.remove('is-open');
      document.removeEventListener('keydown', this);

      this._closeTimer = setTimeout(() => this._afterClose(), 350);
      this._overlay.addEventListener('transitionend', this);

      this.dispatchEvent(new CustomEvent('pdp-info-modal:toggle', { bubbles: true, detail: { open: false } }));
    }

    _afterClose() {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
      if (this._overlay) this._overlay.removeEventListener('transitionend', this);
      document.documentElement.classList.remove('pdp-scroll-lock');
      /* Restore focus to the element that opened the modal */
      if (this._triggerEl && typeof this._triggerEl.focus === 'function') {
        this._triggerEl.focus();
        this._triggerEl = null;
      }
    }

    /** Trap Tab/Shift+Tab within the modal overlay */
    _trapFocus(e) {
      if (!this._overlay) return;
      const focusable = this._overlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    _ensureOverlay() {
      if (this._overlay) return;

      const tpl = document.getElementById('pdp-info-modal-tpl');
      const overlay = tpl.content.firstElementChild.cloneNode(true);
      const header = overlay.querySelector('.pdp-info-modal__header');

      if (this._slots.header) {
        header.innerHTML = this._slots.header;
      } else if (this.dataset.title) {
        const titleEl = header.querySelector('.pdp-info-modal__title');
        if (titleEl) titleEl.textContent = this.dataset.title;
      }

      this._titleEl = overlay.querySelector('.pdp-info-modal__title');
      this._bodyEl = overlay.querySelector('.pdp-info-modal__body');

      if (this._slots.body && this._bodyEl) {
        this._bodyEl.innerHTML = this._slots.body;
      }

      overlay.addEventListener('click', this);
      if (this._customClass) {
        overlay.classList.add(...this._customClass);
      }
      document.body.appendChild(overlay);
      this._overlay = overlay;
    }
  }

  customElements.define('pdp-info-modal', PdpInfoModal);
}
/* ===== PDP Guide Link Web Component ===== */
if (!customElements.get('pdp-guide-link')) {
  class PdpGuideLink extends HTMLElement {
    static _store = new Map();

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('keydown', this);
    }

    handleEvent(e) {
      if (e.type === 'click') {
        this._showModal();
      } else if (e.type === 'keydown' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this._showModal();
      }
    }

    _getModal() {
      const key = this.dataset.storageid;
      let modal = key ? PdpGuideLink._store.get(key) : null;
      if (!modal) {
        modal = document.createElement('pdp-info-modal');
        modal.dataset.title = this.dataset.modalTitle || '';
        modal.setClass(this.dataset.modalClass || '');
        if (key) PdpGuideLink._store.set(key, modal);
      }
      return modal;
    }

    async _showModal() {
      const modal = this._getModal();
      const src = this.dataset.src;

      if (src && !modal._contentLoaded) {
        modal.setContent('<div class="pdp-guide-link__loading"></div>', 'pdp-guide-link__body--loading');
        modal.show();
        try {
          const content = await pdpFetchSection(src);
          modal.setContent(`<div class="pdp-ctx">${content}</div>`, 'pdp-guide-link');
          modal._contentLoaded = true;
        } catch {
          modal.setContent('<p style="padding:20px;text-align:center">Failed to load content.</p>');
        }
      } else {
        modal.show();
      }
    }
  }

  customElements.define('pdp-guide-link', PdpGuideLink);
}
